import * as React from "react";
import { useEffect, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { XCircle, Trash2 } from "lucide-react";
import { SearchInput } from "../common/Table/SearchInput";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";
import { FilterCombobox } from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import TablePagination from "../common/Table/TablePagination";
import { useTableHandlers } from "../common/Table/Hooks/UseTableHandlers";
import JobsRepository, { JobsSortKey } from "../../repositories/JobsRepository";
import { JobRequestStatus, JobRequestStatusColors, JobRequestStatusIcons, JobRequestStatusLabels } from "../../models/Jobs/JobRequestStatus";
import {isDeleteCaseFilesRequest, isDeleteCaseRequest, isDeleteFirmDocumentsFolderRequest, isDeleteFirmDocumentsRequest, isDeleteIntakeFilesRequest, isDeleteIntakeRequest, isDeleteProviderFilesRequest, isDeleteProviderRequest, isDeleteTemplateFilesRequest, isDeleteTemplateRequest, JobRequest} from "../../models/Jobs/JobRequest";
import { JobEntityType, JobEntityTypeColors, JobEntityTypeIcons, JobEntityTypeLabels } from "../../models/Jobs/JobEntityType";
import { JobType, JobTypeColors, JobTypeIcons, JobTypeLabels } from "../../models/Jobs/JobType";
import JobRequestStatusChip from "./JobRequestStatusChip";
import JobTypeChip from "./JobTypeChip";
import JobEntityTypeChip from "./JobEntityTypeChip";
import JobInfoView from "./JobInfoView";
import AbortJobModal from "./AbortJobModal";
import StopJobButton from "./StopJobButton";
import { canAbortJob, canStopJob, isJobFinished } from "../../models/Jobs/JobActions";
import TableEmptyState from "../common/TableEmptyState";
import {useAuth} from "../../contexts/AuthContext";

// Column keys as the table names them, mapped to what the server sorts by.
const SERVER_SORT: Partial<Record<keyof JobRequest, JobsSortKey>> = {
  organizationName: "organization",
  type: "type",
  entityType: "entityType",
  status: "status",
};

export const JobsTable = () => {
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const { getUserId } = useAuth();
  const [sort, setSort] = React.useState<keyof JobRequest>("status");
  const [organizationFilter, setOrganizationFilter] = React.useState<string | null>(null);
  const [jobTypeFilter, setJobTypeFilter] = React.useState<string | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [entityTypeFilter, setEntityTypeFilter] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [currentUserId, setCurrentUserId] = React.useState<string | null>(null);

  const {
    order,
    page,
    rowsPerPage,
    rows,
    setPage,
    setRows,
    changeOrder,
    handleRowsPerPageChange
  } = useTableHandlers({
    order: "asc",
    sort: "status",
    page: 0,
    rowsPerPage: 50
  });

  const jobsQuery = useQuery({
    queryKey: [
      QUERY_KEYS.JOBS, currentUserId, organizationFilter, jobTypeFilter, statusFilter, entityTypeFilter,
      searchQuery, sort, order, page, rowsPerPage,
    ],
    queryFn: async () =>
      await jobsRepository.getJobsPage({
        organizationId: organizationFilter,
        type: jobTypeFilter as JobType | null,
        status: statusFilter as JobRequestStatus | null,
        entityType: entityTypeFilter as JobEntityType | null,
        search: searchQuery.trim(),
        sort: SERVER_SORT[sort] ?? "status",
        descending: order === "desc",
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      }),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    // Every 30 seconds while a job on the page is still going, so its progress and status keep up.
    refetchInterval: (query) =>
      query.state.data?.data?.some((job) => !isJobFinished(job)) ? 30_000 : QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
    // The current page stays on screen while the next one loads, instead of the table emptying on every page turn.
    placeholderData: keepPreviousData,
  });

  const organizationsQuery = useQuery({
    queryKey: [QUERY_KEYS.JOB_ORGANIZATIONS, currentUserId],
    queryFn: async () => await jobsRepository.getJobOrganizations(),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
  });

  const paginatedJobs = jobsQuery.data?.data ?? [];

  useEffect(() => {
    if (jobsQuery.data?.paging)
      setRows(jobsQuery.data.paging.totalCount);
  }, [jobsQuery.data]);

  useEffect(() => setCurrentUserId(getUserId()), []);

  const getQueryInvalidationKeyForJob = (job: JobRequest) => {
    if (isDeleteCaseRequest(job)) {
      const keys: string[] = [QUERY_KEYS.CASES_DELETION_STATUS];
      if (job.status !== JobRequestStatus.Completed)
        keys.push(QUERY_KEYS.CASES);

      return keys;
    } else if (isDeleteCaseFilesRequest(job)) {
      const keys: string[] = [QUERY_KEYS.DELETE_CASE_FILES];
      if (job.status !== JobRequestStatus.Completed)
        keys.push(QUERY_KEYS.CASE_FILES);

      return keys;
    } else if (isDeleteIntakeRequest(job)) {
      const keys: string[] = [QUERY_KEYS.INTAKES_DELETION_STATUS];
      if (job.status !== JobRequestStatus.Completed)
        keys.push(QUERY_KEYS.INTAKES);

      return keys;
    } else if (isDeleteIntakeFilesRequest(job)) {
      const keys: string[] = [QUERY_KEYS.DELETE_INTAKE_FILES];
      if (job.status !== JobRequestStatus.Completed)
        keys.push(QUERY_KEYS.INTAKE_FILES);

      return keys;
    } else if (isDeleteFirmDocumentsFolderRequest(job)) {
      const keys: string[] = [QUERY_KEYS.FIRM_DOCUMENTS_FOLDER_DELETION_STATUS];
      if (job.status !== JobRequestStatus.Completed)
        keys.push(QUERY_KEYS.FIRM_DOCUMENTS_FOLDERS);

      return keys;
    } else if (isDeleteFirmDocumentsRequest(job)) {
      const keys: string[] = [QUERY_KEYS.DELETE_FIRM_DOCUMENTS];
      if (job.status !== JobRequestStatus.Completed)
        keys.push(QUERY_KEYS.FIRM_DOCUMENTS);

      return keys;
    } else if (isDeleteProviderRequest(job)) {
      const keys: string[] = [QUERY_KEYS.PROVIDERS_DELETION_STATUS];
      if (job.status !== JobRequestStatus.Completed)
        keys.push(QUERY_KEYS.PROVIDERS);

      return keys;
    } else if (isDeleteProviderFilesRequest(job)) {
      const keys: string[] = [QUERY_KEYS.DELETE_PROVIDER_FILES];
      if (job.status !== JobRequestStatus.Completed)
        keys.push(QUERY_KEYS.PROVIDER_FILES);

      return keys;
    } else if (isDeleteTemplateRequest(job)) {
      const keys: string[] = [QUERY_KEYS.TEMPLATES_DELETION_STATUS];
      if (job.status !== JobRequestStatus.Completed)
        keys.push(QUERY_KEYS.TEMPLATES);

      return keys;
    } else if (isDeleteTemplateFilesRequest(job)) {
      const keys: string[] = [QUERY_KEYS.DELETE_TEMPLATE_FILES];
      if (job.status !== JobRequestStatus.Completed)
        keys.push(QUERY_KEYS.TEMPLATE_FILES);

      return keys;
    }
  };

  const handleSearch = (newSearchQuery: string) => {
    setPage(0);
    setSearchQuery(newSearchQuery ?? "");
  };

  const changeSort = (newSort: keyof JobRequest) => {
    setPage(0);
    setSort(newSort);
  };

  const organizations = organizationsQuery.data ?? [];

  const filteredStatusOptions = Object.values(JobRequestStatus)
    .filter((status) => status !== JobRequestStatus.New);

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={fileFiltersBoxClassName}>
        <SearchInput label="Search jobs" placeholder="Job name or ID" onSearch={handleSearch} />
        {organizations.length > 0 && (
          <div className={cn(filterComboboxClassName, "w-[220px]")}>
            <Label className="text-xs font-semibold text-muted-foreground">Organization</Label>
            <FilterCombobox
              value={organizationFilter}
              onChange={(newValue) => {
                setPage(0);
                setOrganizationFilter(newValue);
              }}
              placeholder="Any organization"
              searchable
              options={organizations.map((organization) => ({
                value: organization.id,
                label: organization.name,
              }))}
            />
          </div>
        )}
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Job type</Label>
          <FilterCombobox
            value={jobTypeFilter}
            onChange={(newValue) => {
              setPage(0);
              setJobTypeFilter(newValue);
            }}
            placeholder="Job type"
            options={Object.values(JobType).map((type) => {
              const jobType = type as JobType;
              return {
                value: jobType,
                label: JobTypeLabels[jobType],
                color: JobTypeColors[jobType],
                icon: JobTypeIcons[jobType],
              };
            })}
          />
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Status</Label>
          <FilterCombobox
            value={statusFilter}
            onChange={(newValue) => {
              setPage(0);
              setStatusFilter(newValue);
            }}
            placeholder="Status"
            options={filteredStatusOptions.map((status) => {
              const jobRequestStatus = status as JobRequestStatus;
              return {
                value: jobRequestStatus,
                label: JobRequestStatusLabels[jobRequestStatus],
                color: JobRequestStatusColors[jobRequestStatus],
                icon: JobRequestStatusIcons[jobRequestStatus],
              };
            })}
          />
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Entity type</Label>
          <FilterCombobox
            value={entityTypeFilter}
            onChange={(newValue) => {
              setPage(0);
              setEntityTypeFilter(newValue);
            }}
            placeholder="Entity type"
            options={Object.values(JobEntityType).map((jobEntityType) => {
              const entityType = jobEntityType as JobEntityType;
              return {
                value: entityType,
                label: JobEntityTypeLabels[entityType],
                color: JobEntityTypeColors[entityType],
                icon: JobEntityTypeIcons[entityType],
              };
            })}
          />
        </div>
      </div>
      <div className={cn(tableWrapperClassName, (jobsQuery.isFetching || jobsQuery.isError) && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[25%] px-1.5 py-3">
                <SortableTableColumnHeader
                  label={"Organization"}
                  sortProperty={"organizationName"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort as keyof JobRequest)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[10%] px-1.5 py-3">
                <SortableTableColumnHeader
                  label={"Job Type"}
                  sortProperty={"type"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort as keyof JobRequest)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[10%] px-1.5 py-3">
                <SortableTableColumnHeader
                  label={"Entity Type"}
                  sortProperty={"entityType"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort as keyof JobRequest)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[10%] px-1.5 py-3">
                <SortableTableColumnHeader
                  label={"Status"}
                  sortProperty={"status"}
                  getSort={() => sort}
                  setSort={(newSort) => changeSort(newSort as keyof JobRequest)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[45%] px-1.5 py-3">Info</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedJobs.map((job) => {
              // Removing a Failed job stops any retries it had left, except for a deletion, which is stopped instead.
              const canAbort = canAbortJob(job);
              return (
                <TableRow key={job.id}>
                  <TableCell>
                    <span className="text-sm">
                      {job.organizationName}
                    </span>
                  </TableCell>
                  <TableCell>
                    <JobTypeChip type={job.type} />
                  </TableCell>
                  <TableCell>
                    <JobEntityTypeChip type={job.entityType} />
                  </TableCell>
                  <TableCell>
                    <JobRequestStatusChip status={job.status} />
                  </TableCell>
                  <TableCell>
                    <JobInfoView jobRequest={job} />
                  </TableCell>
                  <TableCell>
                    {canStopJob(job) && (
                      <StopJobButton
                        job={job}
                        onStopped={async () => {
                          await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
                          getQueryInvalidationKeyForJob(job)?.forEach((key) =>
                            queryClient.invalidateQueries({ queryKey: [key, job.organizationId] })
                          );
                        }}
                      />
                    )}
                    {canAbort && !canStopJob(job) && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive"
                        aria-label={isJobFinished(job) ? "Remove" : "Cancel"}
                        onClick={async () => {
                          setAbortJob(job);
                        }}
                      >
                        {isJobFinished(job) ? (
                          <Trash2 className="size-4" />
                        ) : (
                          <XCircle className="size-4" />
                        )}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
              {paginatedJobs.length === 0 && !jobsQuery.isFetching && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No jobs found" />
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={jobsQuery.data?.paging?.totalCount ?? 0}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>

      {abortJob && (
        <AbortJobModal
          open={abortJob !== null}
          jobRequest={abortJob}
          onClose={() => setAbortJob(null)}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            const keys = getQueryInvalidationKeyForJob(abortJob);
            if (keys)
              keys.forEach((key) => queryClient.invalidateQueries({ queryKey: [key, abortJob.organizationId] }));

            setAbortJob(null);
          }}
        />
      )}
    </div>
  );
};
